import { ChangeEvent, useCallback, useEffect, useMemo, useRef } from 'react';
import { flushSync } from 'react-dom';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { reorderWithEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/reorder-with-edge';
import { triggerPostMoveFlash } from 'apps/cdp-web-demo/app/(main)/utils/triggerPostMoveFlash';
import {
  authMethodOptions,
  isAuthMethod,
  isAuthMethodData,
} from 'apps/cdp-web-demo/app/data/authMethods';
import { AuthMethod, AuthMethodOption } from 'apps/cdp-web-demo/app/types';
import { ControlGroup } from '@cbhq/cds-web/controls/ControlGroup';
import { Box } from '@cbhq/cds-web/layout';
import { Text } from '@cbhq/cds-web/typography';

import { Collapsible } from '../Collapsible/Collapsible';

import styles from './AuthMethods.module.css';
import { AuthMethodsFieldRow } from './AuthMethodsFieldRow';

type Props = {
  authMethods: AuthMethod[];
  setAuthMethods: (values: AuthMethod[]) => void;
  toggleAuthMethod: (authMethod: AuthMethod, checked: boolean) => void;
};

function AuthMethodsField({ authMethods, setAuthMethods, toggleAuthMethod }: Props) {
  const refMap = useRef(new Map<string, HTMLDivElement | null>());
  const triggerRef = useRef<HTMLButtonElement>(null);

  const registerRef = useCallback((id: AuthMethod, el: HTMLDivElement | null) => {
    refMap.current.set(id, el);
  }, []);

  const selectedAuthMethods = useMemo<AuthMethod[]>(() => {
    return authMethods ?? [];
  }, [authMethods]);

  const handleAuthMethodsSelectionChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value, checked } = e.target;
      if (!isAuthMethod(value)) {
        return;
      }
      toggleAuthMethod(value as AuthMethod, checked);
    },
    [toggleAuthMethod],
  );

  const authMethodsOptions = useMemo<AuthMethodOption[]>(() => {
    const m = new Set([...authMethods, ...Object.keys(authMethodOptions)]);
    const opts: AuthMethodOption[] = [];
    m.forEach((method) => {
      const option = authMethodOptions[method as AuthMethod];
      if (!option) {
        return;
      }
      const Icon = option.icon;
      opts.push({
        value: option.value,
        registerRef,
        children: (
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={16}
                height={16}
              >
                <Icon width={16} role="img" aria-hidden="true" />
              </Box>
              <Text font="label1" lineHeight="headline">
                {option.label}
              </Text>
            </Box>
            {option.disabled && (
              <Text
                font="label1"
                lineHeight="headline"
                color="fgMuted"
                className={styles['coming-soon-label']}
              >
                Coming soon
              </Text>
            )}
          </Box>
        ),
        readOnly: selectedAuthMethods.length === 1 && selectedAuthMethods.includes(option.value),
        disabled: option.disabled,
      });
    });
    return opts;
  }, [authMethods, registerRef, selectedAuthMethods]);

  useEffect(() => {
    return monitorForElements({
      canMonitor({ source }) {
        return isAuthMethodData(source.data);
      },
      onDrop({ location, source }) {
        const target = location.current.dropTargets[0];
        if (!target) {
          return;
        }

        const sourceData = source.data;
        const targetData = target.data;

        if (!isAuthMethodData(sourceData) || !isAuthMethodData(targetData)) {
          return;
        }

        const indexOfSource = authMethods.findIndex((m) => m === sourceData.authMethodId);
        const indexOfTarget = authMethods.findIndex((m) => m === targetData.authMethodId);

        if (indexOfTarget < 0 || indexOfSource < 0) {
          return;
        }

        const closestEdgeOfTarget = extractClosestEdge(targetData);

        // Using `flushSync` so we can query the DOM straight after this line
        flushSync(() => {
          setAuthMethods(
            reorderWithEdge({
              list: authMethods,
              startIndex: indexOfSource,
              indexOfTarget,
              closestEdgeOfTarget,
              axis: 'vertical',
            }),
          );
        });

        const el = sourceData.authMethodId
          ? refMap.current.get(sourceData.authMethodId)
          : undefined;

        if (el instanceof HTMLElement) {
          triggerPostMoveFlash(el);
        }
      },
    });
  }, [authMethods, setAuthMethods]);

  return (
    <Collapsible triggerLabel="Authentication" triggerRef={triggerRef}>
      <ControlGroup
        aria-labelledby={triggerRef.current?.id}
        ControlComponent={AuthMethodsFieldRow}
        value={selectedAuthMethods}
        onChange={handleAuthMethodsSelectionChange}
        options={authMethodsOptions}
        className={styles['control-group']}
      />
    </Collapsible>
  );
}

export { AuthMethodsField };
